/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderNavigationService, BuilderUserContext, REPORT_MOBILE_APP_CHECKLIST, ReportService,
} from '@gh/builder-shared';
import {
  BuilderPermission, JobChecklistProduceDto, JobChecklistsRestService,
  reportIdField,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import { rendererIconByName, textMessageDialogRoute, truthyCellRenderer } from '@gh/common-internal-shared';
import { getRouteInitData } from '@gh/common-shared';
import { ID, PrintEntryPoint } from '@gh/common-shared-data';
import { TranslationService, translationTuple } from '@gh/core-mls';
import {
  DialogService,
  NotificationService,
  TextMessageDialogConfig,
  WaitingService,
} from '@gh/core-ui';
import { GridDefinitionFactory, LayoutBasedGridDefinition } from '@gh/core-ui-grid';
import { filter$, map$, mapTo$, switchMap$, switchMapTo$ } from '@gh/rx/operators';
import { assign, noop } from 'lodash';
import { Observable } from 'rxjs';
import {
  JobChecklist,
  JobChecklistItem,
} from '../../../resources/job-details-tab/job-details-tab-model';
import { JobDppDetailsGeneral } from '../../../resources/job.model';
import { JobDPPBusinessService } from '../../../resources/job-dpp-business.service';
import { openInNewWindow } from '@gh/core-util';

@Component({
             selector: 'gh-job-dpp-details-checklists-tab',
             template: require('./job-dpp-details-checklists-tab.component.html'),
           })
export class JobDppDetailsChecklistsTabComponent implements OnInit {
  gridDefinition: LayoutBasedGridDefinition;
  checklists: JobChecklistItem[];
  jobId: ID;
  details: JobDppDetailsGeneral;
  reportUrl: string;

  get havePermissions(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.VIEW);
  }

  constructor(private route: ActivatedRoute,
              private gridDefinitionFactory: GridDefinitionFactory,
              private navigationService: BuilderNavigationService,
              private businessService: JobDPPBusinessService,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private jobChecklistsRestService: JobChecklistsRestService,
              private dialogService: DialogService,
              private userContext: BuilderUserContext,
              private translationService: TranslationService,
              private report: ReportService) {
  }

  ngOnInit(): void {
    const { gridLayout, checklists, jobId } = getRouteInitData(this.route);
    this.details = this.businessService.jobDetails;
    this.reportUrl = this.businessService.reportUrl;
    assign(this, { checklists, jobId });

    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased({
      isCompleted: {
        cellRenderer: truthyCellRenderer(rendererIconByName('done')),
      },
    });
    this.gridDefinition.bind(gridLayout);
  }

  goToAel(): void {
    this.navigationService.goToAel({
                                     entityId: this.jobId,
                                     entityType: 'JOB',
                                     label: translationTuple('lbl_job_number_with_param', { number: String(this.details.jobNumber) }),
                                   });
  }

  printJob(): void {
    openInNewWindow(this.reportUrl);
  }

  onPreview(item: JobChecklistItem): void {
    this.jobChecklistsRestService.generateImage(this.jobId, item.id).subscribe(() => {
      this.report
        .generateUrl(REPORT_MOBILE_APP_CHECKLIST, [reportIdField('Job', this.jobId), reportIdField('Checklist', item.id)]).pipe(
        this.waitingService.operator()).subscribe(
        (url) => {
          this.previewChecklist(url);
        },
        this.notificationService.processAll);
    });
  }

  onDocsOpen(): void {
    this.businessService.openDocs();
  }

  onOpenSpec(): void {
    this.businessService.openSpec();
  }

  onProduce(item: JobChecklistItem): void {
    this.businessService
      .produceChecklist(PrintEntryPoint.Produce, item).pipe(
      this.waitingService.operator(),
      switchMap$((data) => this.produceChecklist(data)))
      .subscribe(noop, this.notificationService.processAll);
  }

  private produceChecklist(data: JobChecklistProduceDto): Observable<any> {
    const link = data.webViewLink;
    return this.dialogService.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
      title: this.translationService.get('lbl_job_checklist_link_title'),
      html: `${this.translationService.get('lbl_job_checklist_link')}:\n${link}`,
      buttons: [
        { title: this.translationService.get('lbl_ok'), value: 'ok', type: 'primary' },
      ],
    }).afterClosed();
  }

  private previewChecklist(data: string): void {
    if (!this.userContext.hasPermission(REPORT_MOBILE_APP_CHECKLIST.permission)) {
      this.notificationService.error('msg_denied_no_permissions');
    } else {
    openInNewWindow(data);
    }
  }
}
